import { useCallback, useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import "./ProjectStartSection.css";
import { postForm } from "../utils/api";
import CaptchaBadge from "./CaptchaBadge";

const modalConfigs = {
  apply: {
    title: "Apply to Join Our Team",
    // description:
    //   'Share your details and we will reach out with the next steps for joining PinkMoon Technologies.',
    submitLabel: "Submit Application",
    hasResume: true,
    fields: [
      {
        name: "fullName",
        label: "Full Name",
        type: "text",
        placeholder: "Enter your full name",
      },
      {
        name: "email",
        label: "Email Address",
        type: "email",
        placeholder: "Enter your email",
      },
      {
        name: "phone",
        label: "Phone Number",
        type: "tel",
        placeholder: "Enter your phone number",
      },
      {
        name: "role",
        label: "Role of Interest",
        type: "text",
        placeholder: "E.g. AI Engineer, Product Manager",
      },
      {
        name: "message",
        label: "Tell us about yourself",
        type: "textarea",
        placeholder: "Share relevant experience, interests, or questions",
      },
    ],
  },
  conversation: {
    title: "Start a Strategic Conversation",
    description:
      "Tell us about your organisation and how we can collaborate. Our partnerships team will connect with you soon.",
    submitLabel: "Send Request",
    fields: [
      {
        name: "contactName",
        label: "Contact Name",
        type: "text",
        placeholder: "Enter your name",
      },
      {
        name: "company",
        label: "Company / Organisation",
        type: "text",
        placeholder: "Enter your organisation",
      },
      {
        name: "email",
        label: "Business Email",
        type: "email",
        placeholder: "Enter your business email",
      },
      {
        name: "topic",
        label: "Conversation Topic",
        type: "text",
        placeholder: "Share the topic you would like to discuss",
      },
      {
        name: "message",
        label: "Message",
        type: "textarea",
        placeholder: "Provide context, goals, or preferred timelines",
      },
    ],
  },
  involved: {
    title: "Get Involved & Co-Create",
    description:
      "Let us know how you would like to collaborate on innovation initiatives, hackathons, or community programs.",
    submitLabel: "Share Your Interest",
    fields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        placeholder: "Enter your name",
      },
      {
        name: "email",
        label: "Email",
        type: "email",
        placeholder: "Enter your email",
      },
      {
        name: "organisation",
        label: "Organisation / Community",
        type: "text",
        placeholder: "Where are you representing from?",
      },
      {
        name: "focus",
        label: "Focus Area",
        type: "text",
        placeholder: "e.g. Innovation workshops, Student programs",
      },
      {
        name: "message",
        label: "How would you like to collaborate?",
        type: "textarea",
        placeholder: "Tell us your idea or initiative",
      },
    ],
  },
};

const endpointMap = {
  apply: "/api/forms/project/apply",
  conversation: "/api/forms/project/conversation",
  involved: "/api/forms/project/involved",
};

const successMessages = {
  apply: "✅ Thank you! Our talent team will reach out soon.",
  conversation: "✅ Your conversation request has been received.",
  involved: "✅ Thanks for your interest! We will connect with you shortly.",
};

export function ProjectFormModal({ type, onClose }) {
  const config = modalConfigs[type];
  const initialState = useMemo(() => {
    if (!config) return {};
    return config.fields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, {});
  }, [config]);

  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [captchaCode, setCaptchaCode] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const generateCaptcha = useCallback(() => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 6; i += 1) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
    setCaptchaInput("");
  }, []);

  useEffect(() => {
    setValues(initialState);
    setErrors({});
    setStatus(null);
    setResume(null);
    setIsSubmitting(false);
    generateCaptcha();
  }, [generateCaptcha, initialState, type]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!config) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    let finalVal = value;
    if (name === "phone") {
      finalVal = value.replace(/\D/g, "").slice(0, 10);
    }
    setValues((prev) => ({ ...prev, [name]: finalVal }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (errors.submit) {
      setErrors((prev) => ({ ...prev, submit: "" }));
    }
  };

  const handleCaptchaChange = (event) => {
    setCaptchaInput(event.target.value);
    if (errors.captcha) {
      setErrors((prev) => ({ ...prev, captcha: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^\d{10}$/;
    const nameRegex = /^[A-Za-z\s.'-]+$/;

    config.fields.forEach((field) => {
      const val = (values[field.name] || "").trim();
      if (!val) {
        nextErrors[field.name] = `${field.label} is required.`;
      } else if (field.type === "email" || field.name === "email") {
        if (!emailRegex.test(val)) {
          nextErrors[field.name] = "Please enter a valid email address.";
        }
      } else if (field.type === "tel" || field.name === "phone") {
        if (!phoneRegex.test(val)) {
          nextErrors[field.name] = "Phone number must be exactly 10 digits.";
        }
      } else if (
        field.name === "contactName" ||
        field.name === "fullName" ||
        field.name === "name"
      ) {
        if (!nameRegex.test(val)) {
          nextErrors[field.name] = "Name can only contain letters and spaces.";
        } else if (val.length < 2) {
          nextErrors[field.name] = "Name must be at least 2 characters.";
        }
      }
    });

    if (!captchaInput.trim()) {
      nextErrors.captcha = "CAPTCHA is required.";
    } else if (captchaInput.trim().toUpperCase() !== captchaCode.toUpperCase()) {
      nextErrors.captcha = "CAPTCHA code is incorrect.";
    }

    if (config.hasResume && !resume) {
      nextErrors.resume = "Resume PDF is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const endpoint = endpointMap[type];
    if (!endpoint) {
      setErrors((prev) => ({ ...prev, submit: "Unsupported form type." }));
      return;
    }

    setIsSubmitting(true);
    try {
      setErrors((prev) => ({ ...prev, submit: undefined }));

      let payload;
      if (config.hasResume) {
        payload = new FormData();
        Object.entries(values).forEach(([key, val]) =>
          payload.append(key, typeof val === "string" ? val.trim() : val)
        );
        if (resume) payload.append("resume", resume);
      } else {
        payload = {};
        Object.entries(values).forEach(([key, val]) => {
          payload[key] = typeof val === "string" ? val.trim() : val;
        });
      }

      const response = await postForm(endpoint, payload);
      const successMessage =
        response?.message ||
        successMessages[type] ||
        "Submission received successfully.";
      setStatus(successMessage);
      setValues(initialState);
      setResume(null);
      generateCaptcha();
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      console.error("Project form submission failed", error);
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "Something went wrong. Please try again.",
      }));
      generateCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{config.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="modal-close"
          >
            &times;
          </button>
        </div>

        {status ? (
          <div className="modal-success-box">
            <CheckCircle2 size={46} className="modal-success-icon" />
            <h4>Thank You!</h4>
            <p>{status}</p>
          </div>
        ) : (
          <form className="modal-form" onSubmit={handleSubmit} noValidate>
            {errors.submit && (
              <div className="modal-server-error" role="alert">
                {errors.submit}
              </div>
            )}

            {config.fields.map((field) =>
              field.type === "textarea" ? (
                <div key={field.name} className="modal-field">
                  <textarea
                    name={field.name}
                    placeholder={field.placeholder}
                    value={values[field.name] || ""}
                    onChange={handleChange}
                    className={errors[field.name] ? "modal-input-error" : ""}
                  />
                  {errors[field.name] && (
                    <span className="modal-field-error">
                      {errors[field.name]}
                    </span>
                  )}
                </div>
              ) : (
                <div key={field.name} className="modal-field">
                  <input
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={values[field.name] || ""}
                    onChange={handleChange}
                    maxLength={field.name === "phone" ? 10 : undefined}
                    className={errors[field.name] ? "modal-input-error" : ""}
                  />
                  {errors[field.name] && (
                    <span className="modal-field-error">
                      {errors[field.name]}
                    </span>
                  )}
                </div>
              )
            )}

            {config.hasResume && (
              <div className="modal-field">
                <label
                  style={{
                    fontSize: "0.85rem",
                    color: "#475569",
                    marginBottom: "4px",
                    display: "block",
                    fontWeight: "600",
                  }}
                >
                  Upload Resume (PDF) *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    setResume(e.target.files[0]);
                    if (errors.resume)
                      setErrors((prev) => ({ ...prev, resume: "" }));
                  }}
                  className={errors.resume ? "modal-input-error" : ""}
                />
                {errors.resume && (
                  <span className="modal-field-error">{errors.resume}</span>
                )}
              </div>
            )}

            <div className="modal-field">
              <div className="modal-captcha-row">
                <CaptchaBadge
                  code={captchaCode}
                  width={90}
                  height={38}
                  bgColor="#1e293b"
                  textColor="#f8fafc"
                  fontSize={18}
                />
                <button
                  type="button"
                  className="modal-captcha-refresh"
                  onClick={generateCaptcha}
                  title="Refresh CAPTCHA"
                  aria-label="Refresh CAPTCHA"
                >
                  ↻
                </button>
                <input
                  type="text"
                  name="captcha"
                  placeholder="Enter CAPTCHA"
                  value={captchaInput}
                  onChange={handleCaptchaChange}
                  onPaste={(e) => e.preventDefault()}
                  autoComplete="off"
                  className={`modal-captcha-input ${
                    errors.captcha ? "modal-input-error" : ""
                  }`}
                  maxLength={6}
                  aria-label="Enter CAPTCHA"
                />
                <button
                  type="submit"
                  className="modal-submit modal-submit-inline"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span>Sending...</span>
                      <Loader2 size={16} className="cs-spinner-icon" />
                    </>
                  ) : (
                    <span>{config.submitLabel}</span>
                  )}
                </button>
              </div>
              {errors.captcha && (
                <span className="modal-field-error">{errors.captcha}</span>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ProjectStartSection() {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
    if (activeModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => document.body.classList.remove("modal-open");
  }, [activeModal]);

  const openModal = (type) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);

  return (
    <section className="project-start-section">
      <div className="project-start-container">
        <div className="project-start-header section-header">
          <div className="project-start-label section-label">
            <svg
              className="project-start-label-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                fill="currentColor"
              />
            </svg>
            <span>Business Collaborations</span>
          </div>
          <h2 className="project-start-title section-title">
            Would you like to start a Journey with us?
          </h2>
          <p className="project-start-description section-description">
            Experience how our AI-powered features simplify workflows, automate
            routine tasks, and help your team achieve more—so you can focus on
            growing your business.
          </p>
        </div>

        <div className="project-start-cards">
          <div
            className="project-card project-card-large"
            onClick={() => navigate("/careers")}
          >
            <div className="project-card-content">
              <Link
                to="/careers"
                className="project-card-link project-card-icon-link"
                title="Go to Careers"
                aria-label="Go to Careers"
                onClick={(event) => event.stopPropagation()}
              >
                <svg
                  className="project-card-arrow"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 17L17 7M17 7H7M17 7V17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
              <div className="project-card-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
              <h3 className="project-card-title">Join Our Team</h3>
              <p className="project-card-description">
                Become part of a passionate and innovative group of
                professionals.
              </p>
            </div>
            <div className="project-card-image">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop&q=80"
                alt="Join Our Team"
              />
            </div>
          </div>

          <div
            className="project-card project-card-large"
            onClick={() => openModal("conversation")}
          >
            <div className="project-card-content">
              <button
                type="button"
                className="project-card-link project-card-icon-link"
                title="Start a Strategic Conversation"
                aria-label="Start a Strategic Conversation"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openModal("conversation");
                }}
              >
                <svg
                  className="project-card-arrow"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 17L17 7M17 7H7M17 7V17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="project-card-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 12H16M8 12L5 9M8 12L5 15M16 12L19 9M16 12L19 15"
                    stroke="#27ae60"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    stroke="#27ae60"
                    strokeWidth="2"
                    fill="none"
                  />
                  <path
                    d="M12 3V21M3 12H21"
                    stroke="#27ae60"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h3 className="project-card-title">Partner With Us</h3>
              <p className="project-card-description">
                Explore strategic partnerships and win-win collaborations.
              </p>
            </div>
            <div className="project-card-image">
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&h=400&fit=crop&q=80"
                alt="Partner With Us"
              />
            </div>
          </div>

          <div
            className="project-card project-card-large"
            onClick={() => openModal("involved")}
          >
            <div className="project-card-content">
              <button
                type="button"
                className="project-card-link project-card-icon-link"
                title="Get Involved & Co-Create"
                aria-label="Get Involved & Co-Create"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openModal("involved");
                }}
              >
                <svg
                  className="project-card-arrow"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 17L17 7M17 7H7M17 7V17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="project-card-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C12 2 11 4 11 6C11 8 12 9 12 9C12 9 13 8 13 6C13 4 12 2 12 2Z"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M12 9V21"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 13H16"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 17H15"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 21H14"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="project-card-title">Co-Create Ideas</h3>
              <p className="project-card-description">
                Join innovation workshops and co-develop solutions that matter.
              </p>
            </div>
            <div className="project-card-image">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop&q=80"
                alt="Co-Create Ideas"
              />
            </div>
          </div>
        </div>
      </div>
      {activeModal && (
        <ProjectFormModal type={activeModal} onClose={closeModal} />
      )}
    </section>
  );
}

export default ProjectStartSection;
